import {
  Dokument,
  SchadensArt,
  SchadenUpload,
  UserData,
} from '@codefluegel/wowis-rest-typescript-client';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Snackbar,
  styled,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import imageCompression from 'browser-image-compression';
import { action, autorun } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import moment from 'moment';
import { useDocumentApi, useSchadenApi } from '../../ApiClient';
import uploadDocument from '../../helpers/UploadHelper';
import LocalizedDatePicker from '../datepicker/DatePicker';
import LoadingSpinner from '../spinner/LoadingSpinner';

const SelectStyled = styled(Select)({
  color: '#58585A',
});

const MenuItemStyled = styled(MenuItem)({
  color: '#58585A',
});

interface DamageReport {
  title: string;
  images: Array<File>;
  localImages: string[];
  text: string;
  type: string;
  checked: boolean;
  reportType: string;
  phone: string;
  acceptTerms: string;
  date: Date;
}

const AddReportsModal = observer(
  ({
    open,
    onClose,
    customerId,
    objId,
    user,
  }: {
    open: boolean;
    customerId: number;
    objId: number;
    onClose: () => void;
    user: UserData | undefined;
  }): JSX.Element => {
    const damageApi = useSchadenApi();
    const documentApi = useDocumentApi();

    const [filteredDamageTypes, setFilteredDamageTypes] = useState<
      SchadensArt[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [snackBar, setSnackBar] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    const {
      control,
      handleSubmit,
      setValue,
      formState: { errors },
    } = useForm<DamageReport>();

    const formData = useLocalObservable(() => ({
      images: Array.of<File>(),
      localImages: [''],
      type: 'Wohnung',
      phone: user?.phone ?? '',
      checked: false,
      damageTypesList: [] as SchadensArt[],
    }));

    useEffect(
      () =>
        autorun(() => {
          async function getDamageReports() {
            const { data } = await damageApi.getAllSchadensArten(customerId);
            formData.damageTypesList = data;
            setFilteredDamageTypes(
              formData.damageTypesList.filter(type =>
                type.schad_gruppe.includes(formData.type),
              ),
            );
          }
          if (formData.damageTypesList.length === 0 && customerId)
            getDamageReports();
        }),
      [damageApi, formData],
    );

    const handleClose = () => {
      onClose();
    };

    const saveEntry = async (values: DamageReport) => {
      setLoading(true);
      try {
        let images: Dokument[] = [];

        if (formData.images.length > 0) {
          images = await Promise.all(
            formData.images
              ?.filter(image => image.name !== '')
              .map(image => uploadDocument(image, documentApi)),
          );
        }

        const schadenData: SchadenUpload = {
          schad_gruppe: [formData.type],
          schad_telnr: values.phone,
          schad_art: values.text,
          schad_art_id: Number(values.reportType),
          schad_datum: values.date.toISOString(),
          schad_bilder: images,
          schad_beschr: values.title,
        };

        await damageApi.addSchaden(objId, customerId, schadenData);
        onClose();
      } catch (error) {
        console.error(error);
        setErrorMsg(
          'Die Schadensmeldung konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.',
        );
        setSnackBar(true);
      } finally {
        setLoading(false);
      }
    };

    const captureFile: React.ChangeEventHandler<HTMLInputElement> = action(
      async ({ target: input }) => {
        if (!input.files) return;

        const options = {
          maxSizeMB: 3,
          maxWidthOrHeight: 1280,
          useWebWorker: true,
        };

        const compressedFiles = await Promise.all(
          Array.from(input.files).map(file => imageCompression(file, options)),
        );

        formData.localImages = compressedFiles.map(file =>
          URL.createObjectURL(file),
        );
        formData.images = compressedFiles;
      },
    );

    // close snackbar
    const handleSnackClose = () => {
      setSnackBar(false);
      setErrorMsg('');
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="#58585A">
          Erstellen Sie eine neue Schadensmeldung
        </DialogTitle>
        <DialogContent>
          <hr />
          {formData.damageTypesList.length > 0 && (
            <FormControl sx={{ marginTop: 2, marginBottom: 2 }}>
              <FormLabel component="legend">Schaden betrifft</FormLabel>
              <RadioGroup
                row
                color="#58585A"
                aria-label="gender"
                name="row-radio-buttons-group"
                onChange={action((event: SelectChangeEvent<string>) => {
                  formData.type = event.target.value;
                  setValue('reportType', ''); // Reset reportType on radio change
                  setFilteredDamageTypes(
                    formData.damageTypesList.filter(type =>
                      type.schad_gruppe.includes(event.target.value),
                    ),
                  );
                })}
                defaultValue="Wohnung"
              >
                <FormControlLabel
                  value="Wohnung"
                  control={<Radio />}
                  label={
                    <Typography color="secondary"> Ihre Wohnung </Typography>
                  }
                />
                <FormControlLabel
                  value="Allgemeinbereich"
                  control={<Radio />}
                  label={
                    <Typography color="secondary">
                      Allgemeinbereich/Außenanlage
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}
          <Controller
            name="reportType"
            control={control}
            rules={{ required: 'Pflichtfeld!' }}
            defaultValue=""
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl
                fullWidth
                error={!!error}
                required
                sx={{ marginTop: 2 }}
              >
                <FormLabel component="legend">Schaden wählen</FormLabel>
                <SelectStyled
                  fullWidth
                  color="secondary"
                  value={value || ''}
                  onChange={onChange}
                  error={!!error}
                >
                  {filteredDamageTypes.map(type => (
                    <MenuItemStyled
                      key={type.schad_art_id}
                      value={type.schad_art_id}
                      color="#58585A"
                    >
                      {type.schad_art}
                    </MenuItemStyled>
                  ))}
                </SelectStyled>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />

          <Controller
            name="title"
            control={control}
            rules={{ required: 'Pflichtfeld!' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  input: { color: '#58585A' },
                }}
                margin="dense"
                id="content"
                label="Schadensbeschreibung"
                type="text"
                fullWidth
                placeholder="Beschreiben Sie den Schaden in 150 Zeichen."
                variant="standard"
                rows={4}
                inputProps={{
                  maxLength: 150,
                  style: { color: '#58585A' },
                }}
                multiline
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : null}
                required
              />
            )}
          />

          <Box
            sx={{
              my: 2,
            }}
          >
            <Controller
              name="date"
              control={control}
              rules={{
                required: 'Pflichtfeld!',
                validate: value => {
                  if (!value || !moment(value).isValid()) {
                    return 'Bitte wählen Sie ein gültiges Datum';
                  }
                  return true;
                },
              }}
              defaultValue={new Date()}
              render={({ field: { onChange }, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  error={!!error}
                  required
                  sx={{ marginTop: 2 }}
                >
                  <FormLabel component="legend">Bemerkt am:</FormLabel>
                  <LocalizedDatePicker
                    onClose={date => {
                      onChange(date);
                    }}
                  />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>

          <Controller
            name="phone"
            control={control}
            defaultValue={user?.phone ?? ''}
            rules={{ required: 'Pflichtfeld!' }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  input: { color: '#58585A' },
                }}
                id="phone"
                label="Telefonnummer "
                type="text"
                inputProps={{ maxLength: 20 }}
                variant="standard"
                value={value}
                error={!!error}
                onChange={onChange}
                helperText={error ? error.message : null}
                required
              />
            )}
          />
          <Box sx={{ marginTop: 2, marginBottom: 2 }}>
            <label htmlFor="upload-report">
              Bild hinzufügen
              <input
                accept="image/*"
                id="upload-report"
                type="file"
                hidden
                multiple
                onChange={captureFile}
              />
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
              >
                <PhotoCamera />
              </IconButton>
            </label>
          </Box>
          {formData.localImages.map(uri => (
            <img src={uri} key={uri} alt={uri} width="300" />
          ))}

          <FormControl
            required
            error={!!errors.acceptTerms}
            component="fieldset"
            sx={{ mt: 2 }}
          >
            <FormLabel component="legend">Einverständniserklärung</FormLabel>
            <FormControlLabel
              sx={{ my: 1, color: '#58585A' }}
              control={
                <Controller
                  control={control}
                  name="acceptTerms"
                  rules={{ required: 'Pflichtfeld!' }}
                  render={({ field: { onChange } }) => (
                    <Checkbox
                      color="primary"
                      onChange={action(e => {
                        onChange(e.target.checked);
                        formData.checked = e.target.checked;
                      })}
                      required
                      value={formData.checked}
                    />
                  )}
                />
              }
              label="Ich bin damit einverstanden, dass meine Kontaktdaten zum Zwecke der
               Schadensbehebung an beauftragte Dienstleister zur zweckmäßigen Verwendung 
               übermittelt werden."
            />
            {errors.acceptTerms && (
              <FormHelperText>Pflichtfeld!</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        {loading && <LoadingSpinner />}
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button disabled={loading} onClick={handleSubmit(saveEntry)}>
            Speichern
          </Button>
        </DialogActions>
        <Snackbar
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          open={snackBar}
          autoHideDuration={6000}
          onClose={handleSnackClose}
          message={errorMsg}
        >
          <Alert severity="error">{errorMsg}</Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default AddReportsModal;
